import { useEffect, useCallback, useMemo } from 'react';
import { t } from 'services/i18n.service';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { FOCUS_TEMPLATE_FEATURE, TAB } from 'constants/general';
import { TabBodyWrapper } from '../TabBodyWrapper';
import PackAndFocusModeInfoContainer from 'dashboard/wrappers/PackAndFocusModeInfoContainer';
import PackAndFocusModeInfoLeftContainer from 'dashboard/wrappers/PackAndFocusModeInfoLeftContainer';
import PackAndFocusModeInfoRightContainer from 'dashboard/wrappers/PackAndFocusModeInfoRightContainer';
import InputWithLabel from '../../../common/inputs/InputWithLabel';
import RichTextEditorWithLabel from 'components/common/RichTextEditorWithLabel';
import AdminWidgets from './AdminWidgets';
import { updateFilterIsAdminToolbarShow } from 'store/reducer/filter/slice';
import {
  changePackDescription,
  changePackDescriptionVideo,
  changePackName,
  changePackWelcomeMessage,
  changePackWelcomeMessageVideo
} from 'store/reducer/setting/slice';
import { updateCurrentViewedFocusTemplate } from 'store/reducer/focus-mode/slice';
import { packAndFocusModeInfoSelector } from 'store/reducer/focus-mode/selectors';

const PackAndFocusModeInfo = () => {
  const dispatch = useAppDispatch();
  const {
    currentSettings,
    tabs,
    activeTabIndex,
    isUserAdmin,
    currentViewedFocusTemplate
  } = useAppSelectorShallow(packAndFocusModeInfoSelector);

  useEffect(() => {
    isUserAdmin && dispatch(updateFilterIsAdminToolbarShow(true));
  }, [dispatch, isUserAdmin]);

  const isPackInfoTab = useMemo(
    () => tabs[activeTabIndex] === TAB.PACK_INFO,
    [tabs, activeTabIndex]
  );

  const {
    title,
    nameLabel,
    descriptionURLLabel,
    descriptionURLTooltip,
    descriptionLabel,
    descriptionTooltip,
    welcomeURLLabel,
    welcomeURLTooltip,
    welcomeLabel,
    welcomeTooltip,
    name,
    descriptionVideoUrl,
    description,
    welcomeVideoURL,
    welcomeMessage
  } = useMemo(() => {
    const tPrefix = isPackInfoTab ? 'packs' : 'focus_mode';
    const namePrefix = isPackInfoTab ? 'pack_' : '';
    const data = isPackInfoTab ? currentSettings : currentViewedFocusTemplate;

    return {
      title: t(
        isPackInfoTab
          ? 'enter_habit_pack_info'
          : 'focus_mode.enter_focus_mode_info'
      ),
      nameLabel: t(`${tPrefix}.${namePrefix}name`),
      descriptionURLLabel: t(`${tPrefix}.${namePrefix}description_url`),
      descriptionURLTooltip: t(`${tPrefix}.description_url_tooltip`),
      descriptionLabel: t(`${tPrefix}.${namePrefix}description`),
      descriptionTooltip: t(`${tPrefix}.description_tooltip`),
      welcomeURLLabel: t(`${tPrefix}.${namePrefix}welcome_video_url`),
      welcomeURLTooltip: t(`${tPrefix}.welcome_url_tooltip`),
      welcomeLabel: t(`${tPrefix}.${namePrefix}welcome_message`),
      welcomeTooltip: t(`${tPrefix}.welcome_message_tooltip`),
      name: isPackInfoTab
        ? currentSettings?.pack_name
        : currentViewedFocusTemplate?.name,
      descriptionVideoUrl: data?.description_video_url,
      description: data?.description,
      welcomeVideoURL: data?.welcome_video_url,
      welcomeMessage: data?.welcome_message
    };
  }, [isPackInfoTab, currentSettings, currentViewedFocusTemplate]);

  const handleChange = useCallback(
    (feature: string, value: string | boolean) => {
      if (isPackInfoTab) {
        switch (feature) {
          case FOCUS_TEMPLATE_FEATURE.NAME:
            dispatch(changePackName(value as string));
            break;
          case FOCUS_TEMPLATE_FEATURE.DESCRIPTION:
            dispatch(changePackDescription(value as string));
            break;
          case FOCUS_TEMPLATE_FEATURE.DESCRIPTION_VIDEO_URL:
            dispatch(changePackDescriptionVideo(value as string));
            break;
          case FOCUS_TEMPLATE_FEATURE.WELCOME_VIDEO_URL:
            dispatch(changePackWelcomeMessageVideo(value as string));
            break;
          case FOCUS_TEMPLATE_FEATURE.WELCOME_MESSAGE:
            dispatch(changePackWelcomeMessage(value as string));
            break;
          default:
            break;
        }
      } else {
        dispatch(
          updateCurrentViewedFocusTemplate({
            focusTemplateFeature: feature,
            value
          })
        );
      }
    },
    [isPackInfoTab, dispatch]
  );

  return (
    <TabBodyWrapper>
      {title ? (
        <h2 className='~text-xs/lg font-bold ~pb-2/2.5 ~pt-4/5'>{title}</h2>
      ) : null}
      <PackAndFocusModeInfoContainer>
        <PackAndFocusModeInfoLeftContainer>
          <InputWithLabel
            label={nameLabel}
            handleChange={({ target: { value } }) =>
              handleChange(FOCUS_TEMPLATE_FEATURE.NAME, value)
            }
            value={name ?? ''}
            type={t('name')}
            required
          />
          <InputWithLabel
            label={descriptionURLLabel}
            tooltip={descriptionURLTooltip}
            handleChange={({ target: { value } }) =>
              handleChange(FOCUS_TEMPLATE_FEATURE.DESCRIPTION_VIDEO_URL, value)
            }
            value={descriptionVideoUrl ?? ''}
            required
            type='url'
          />
          <RichTextEditorWithLabel
            label={descriptionLabel}
            tooltip={descriptionTooltip}
            handleChange={(value) =>
              handleChange(FOCUS_TEMPLATE_FEATURE.DESCRIPTION, value)
            }
            value={description ?? ''}
            required
          />
        </PackAndFocusModeInfoLeftContainer>
        <PackAndFocusModeInfoRightContainer>
          <InputWithLabel
            label={welcomeURLLabel}
            tooltip={welcomeURLTooltip}
            handleChange={({ target: { value } }) =>
              handleChange(FOCUS_TEMPLATE_FEATURE.WELCOME_VIDEO_URL, value)
            }
            value={welcomeVideoURL ?? ''}
            type='url'
          />
          <RichTextEditorWithLabel
            label={welcomeLabel}
            tooltip={welcomeTooltip}
            handleChange={(value) =>
              handleChange(FOCUS_TEMPLATE_FEATURE.WELCOME_MESSAGE, value)
            }
            value={welcomeMessage ?? ''}
          />
        </PackAndFocusModeInfoRightContainer>
        {isUserAdmin && <AdminWidgets />}
      </PackAndFocusModeInfoContainer>
    </TabBodyWrapper>
  );
};

export default PackAndFocusModeInfo;
